/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosInstance} from 'axios';
import {parse} from '@ion/core/src/services/ConnectUtils';
import {
  AxiosConfig
} from '@ion/core/src/types/services';
import {
  PreAuthTokenConfig,
  PreAuthTokenResponse
} from './types';

axios.defaults.withCredentials = true;

/**
 * Defines a reference to the configured Axios HTTP Auth client instance.
 */
export let client: AxiosInstance;

/**
 * Defines a reference to the WS Auth Configuration which is used by all
 * requests.
 */
export let config: AxiosConfig;

/**
 * The getPreAuthToken function retrieves the Single Use Auth Token from the
 * Gateway API Services which is required when initiating the underlying WebSocket
 * handshake.
 *
 * ```typescript
 *    import {getHandshakeToken} from '@ion/services/src/websocket/Auth';
 *
 *    const temp = await getHandshakeToken(authToken);
 *
 * ```
 */
export const getPreAuthToken = async(authToken: string): Promise<string> => {
  const result = await client({
    url     : config.baseURL,
    method  : 'post',
    headers : {
      ...config.headers,
      Authorization : authToken
    }
  }) as unknown as PreAuthTokenResponse;
  return parse(result, 'authToken') as unknown as string;
};

/**
 * The setAuthConfig function allows for initializing the configuration
 * of the Auth Client instance.
 *
 * ```typescript
 *
 *    import {loadConfig} from '@ion/core/src/environment/environment';
 *    import {setAuthConfig} from '@ion/core/src/services/ArcGIS';
 *
 *    loadConfig(
 *      config => {
 *        setAuthConfig(config.websocketTokenURL, config.appKey);
 *        ...
 *      },
 *      ...
 *    );
 *
 * ```
 */
export const setPreAuthConfig = (configuration: PreAuthTokenConfig): AxiosInstance => {
  config = {
    withCredentials : true,
    baseURL         : configuration.websocketTokenURL,
    timeout         : 60000,
    headers         : {
      'x-app-key'    : configuration.appKey,
      'Content-Type' : 'application/json'
    }
  };
  client = axios.create(config);
  client.defaults.withCredentials = true;
  return client;
};
